import { graphql } from 'react-relay';
import React, { useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { styled, css } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import IconButton from '@atlaskit/button/icon/button';
import type { NewCoreIconProps } from '@atlaskit/icon/types';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss } from '@atlaskit/primitives/xcss';
import StatusSuccessIcon from '@atlaskit/icon/core/status-success';
import usePressTracing from '@atlaskit/react-ufo/use-press-tracing';
import ReplyLeftIcon from '@atlaskit/icon-lab/core/reply-left';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { MutationUpdaterInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import {
	VIEW_REACTIONS_EXPERIENCE,
	DELETE_INLINE_COMMENT_EXPERIENCE,
	DELETE_PAGE_COMMENT_EXPERIENCE,
	EDIT_PAGE_COMMENT_EXPERIENCE,
	EDIT_PAGE_COMMENT_LOAD_EXPERIENCE,
	EDIT_PAGE_COMMENT_PUBLISH_EXPERIENCE,
	EDIT_INLINE_COMMENT_EXPERIENCE,
	EDIT_INLINE_COMMENT_LOAD_EXPERIENCE,
	EDIT_INLINE_COMMENT_PUBLISH_EXPERIENCE,
	RESOLVE_INLINE_COMMENT_EXPERIENCE,
	RESOLVE_PAGE_COMMENT_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { getInlineCommentExperienceContext } from '@confluence/experience-tracker/entry-points/getInlineCommentExperienceContext.ts';
import { startEditorExperiences } from '@confluence/experience-tracker/entry-points/startExperienceForRoute.ts';
import { useUpdateReactionInCache } from '@confluence/comments-data-manager/entry-points/useUpdateReactionInCache.ts';
import { DisabledCommentAction } from '@confluence/comment/entry-points/DisabledCommentAction.ts';
import { fg } from '@confluence/feature-gating';
import { CommentWarningDialog } from '@confluence/comment-dialogs';
import type {
	ReactionsData,
	ReactionsMutationResults,
} from '@confluence/reactions/entry-points/reactionTypes.ts';
import {
	ReactionContainerType,
	ReactionLocation,
} from '@confluence/reactions/entry-points/reactionTypes.ts';
import { Reactions } from '@confluence/reactions/entry-points/Reactions.ts';
import {
	useCommentsContentState,
	useCommentsContentActions,
} from '@confluence/comment-context/entry-points/useCommentsContent.ts';
import { ReactionsContext } from '@confluence/comment-context/entry-points/ReactionsContext.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { getCachedPageData } from '@confluence/live-pages-utils/entry-points/getCachedPageData.ts';
import { useContentType, usePageContentId } from '@confluence/page-context';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import {
	type CommentReactionsSummary as ReactionsSummary,
	type CommentInlineResolveProperties,
	type FooterResolvedProperties,
} from '@confluence/comments-data-manager/entry-points/useAllCommentsData.ts';
import { CommentType } from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils.ts';
import { copyToClipboard } from '@confluence/comments-util/entry-points/domUtils.ts';
import { AnalyticsSource, ActionName } from '@confluence/comments-util/entry-points/analytics.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { useCommentService } from '@confluence/content-types-bridge-comments';
import { preloadDataRetentionPolicy } from '@confluence/data-lifecycle-management/entry-points/queries.ts';
import { commentsi18n } from '@confluence/comments-util/entry-points/commentsi18n.ts';

import { InlineCommentMode } from '../enum/InlineCommentMode.tsx';
import { InlineCommentFramework } from '../enum/InlineCommentFramework.tsx';
import type {
	CommentPermissions,
	InlineCommentsMode,
	CommentAction,
} from '../inlineCommentsTypes.ts';
import { ActionsContainer } from '../styled-components.tsx';
import { i18n } from '../inlineCommentsi18n.ts';
import { inlineCommentExists } from '../inlineCommentsCommonUtils.ts';
import { ReplyButton } from '../ReplyButton.tsx';

import type {
	useCommentActionsReactionsCommentsQuery as useCommentActionsReactionsCommentsQuery$data,
	useCommentActionsReactionsCommentsQueryVariables as useCommentActionsReactionsCommentsQuery$variables,
} from './__types__/useCommentActionsReactionsCommentsQuery.ts';
import { useCommentReply } from './useCommentReply.ts';
import { getOptimisticResponse } from './optimisticResponseHelpers.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
const useCommentActionsReactionsCommentsQuery = graphql`
	query useCommentActionsReactionsCommentsQuery($contentId: ID!, $containerId: ID!) {
		comments(commentId: $contentId, contentStatus: [DRAFT, CURRENT]) {
			nodes {
				id
				reactionsSummary(pageId: $containerId, contentType: "page", childType: "comment") {
					reactionsCount
					ari
					containerAri
					reactionsSummaryForEmoji {
						emojiId
						count
						reacted
						id
					}
				}
			}
		}
	}
`;

const reactionBelowCommentBodyStyle = xcss({
	marginTop: 'space.0',
});

const itemStyles = css({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	gap: token('space.075'),
});

const reactionInCommentPanelStyle = xcss({
	paddingTop: 'space.050',
	marginLeft: 'space.200',
	paddingLeft: 'space.300',
	paddingBottom: 'space.100',
});

const panelReactionMarginStyle = xcss({
	marginLeft: 'space.150',
});

const replyReactionInCommentPanelStyle = xcss({
	marginLeft: 'space.0',
	paddingLeft: 'space.100',
	paddingTop: 'space.0',
	paddingBottom: 'space.0',
});

const standaloneReactionMarginStyle = xcss({
	marginLeft: 'space.0',
});

const withLeftBorderStyle = xcss({
	borderLeft: `1.5px solid ${token('color.background.accent.gray.subtlest.hovered')}`,
});

const withLeftBorderStyleUpdated = xcss({
	borderLeft: `${token('border.width')} solid ${token(
		'color.background.accent.gray.subtlest.hovered',
	)}`,
});

const customPaddingStyle = xcss({
	paddingLeft: 'space.0',
});

const multipleActionsStyle = xcss({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'start',
	gap: 'space.150',
});

const standaloneParentStyle = xcss({
	marginLeft: 'space.150',
	paddingLeft: 'space.250',
});

const standaloneParentWithNoRepliesStyle = xcss({
	marginLeft: 'space.150',
	paddingLeft: 'space.300',
});

// As footer does not have isDangling
type CustomProps = (
	| Omit<CommentInlineResolveProperties, 'isDangling'>
	| FooterResolvedProperties
) & {
	isDangling?: boolean;
	resolved?: boolean;
	resolvedTime?: number | null;
};

export type UseCommentActionsProps = {
	agentChatMenuItem?: React.ReactNode;
	pageId: string;
	pageType: string;
	commentId: string;
	permissions: CommentPermissions;
	isReply: boolean;
	parentCommentId?: string | null;
	restrictEdit?: boolean;
	restrictDelete?: boolean;
	deleteComment?: () => void;
	editComment?: () => void;
	resolveComment?: () => void;
	numReplies?: number;
	mode: InlineCommentsMode;
	supportedActions: CommentAction[];
	isCommentsPanel?: boolean;
	isHovered?: boolean;
	annotationId?: string;
	resolveProperties?: CustomProps | null;
	reopenComment?: () => void;
	replyToComment?: (commentId: string) => void;
	inheritedReactionsData?: ReactionsSummary | null;
	reactionsReadOnly?: boolean;
	reactionsCacheUpdateFn?: (
		emojiId: string,
		actionType: 'add' | 'delete',
		contentId: string,
		containerId?: string,
	) => MutationUpdaterInterop<any> | void;
	commentDateUrl?: string;
	commentType: CommentType;
	hideBranchingStyle?: boolean;
	canAddComments?: boolean;
	isSelected?: boolean;
	isParentCommentOpen?: boolean;
	isFooterComment?: boolean;
	isStandaloneComment?: boolean;
	isOffline?: boolean;
	isFabricPage?: boolean;
	authorName?: string | null;
	isArchived?: boolean;
};

// WS-2470: We need to override this for correct placement of disabled actions in the overflow menu
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OverflowDisabledDropdownItem = styled(DropdownItem)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	padding: '0 !important',
});

const clickableItemStyle = xcss({
	cursor: 'pointer',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const ReplyContainer = styled.div({
	alignContent: 'center',
	cursor: 'pointer',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const ReactionsContainer = styled.div<{
	hasReactions: boolean;
	isCommentsPanel: boolean;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	display: (props) => (props.hasReactions ? 'block' : 'inline-flex'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	flexBasis: (props) => (props.hasReactions ? '100%' : 'auto'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	marginTop: (props) =>
		!props.isCommentsPanel && !props.hasReactions ? token('space.050') : token('space.0'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	marginLeft: (props) =>
		!props.isCommentsPanel && !props.hasReactions ? token('space.050') : token('space.0'),
});

type ActionItemProps = {
	mode?: InlineCommentsMode;
	isReply?: boolean;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const ActionItem = styled.li<ActionItemProps>(`
  display: inline-flex;
  flex-flow: row nowrap;
  color: ${token('color.text.subtle')};
  min-height: 24px;
  align-items: center;

  a {
    align-items: flex-end;
    color: ${token('color.text.brand')};
    flex: 2;
    text-align: right;
    font: ${token('font.body.small')};
    padding-top: ${token('space.050')};
    cursor: pointer;
  }

  & button {
    color: ${token('color.text.subtle')};
  }

  & button:focus {
    outline: 2px solid ${token('color.border.focused')};
  }

  &:not(:last-of-type):after {
    content: "•";
    color: ${token('color.text.subtle')};
    padding: 0 ${token('space.050')} 0 ${token('space.050')};
  }

  &:first-of-type:after {
    display: none;
  }
`);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const ActionButton = styled.button(`
  background: none;
  color: inherit;
  border: none;
  padding: 0;
  font-size: inherit;
  cursor: pointer;
  outline: inherit;

  &:focus {
    outline: 2px solid ${token('color.border.focused')};
    outline-offset: 2px;
  }

  :hover span {
    text-decoration: underline;
  }
`);

const rightAlignedActionItemsStyle = xcss({
	position: 'absolute',
	right: 'space.0',
	display: 'flex',
	gap: 'space.075',
	justifyContent: 'space-between',
	alignItems: 'center',
	paddingLeft: 'space.150',
});

const rightAlignedActionItemsCommentsPanelStyle = xcss({
	top: 'space.0',
});

const rightAlignedItemsStandaloneStyle = xcss({
	top: 'space.150',
	right: 'space.200',
});

const rightAlignedActionItemsFooterCommentStyle = xcss({
	top: 'space.100',
});

const commentsPanelActionItemsStyle = xcss({
	top: 'space.050',
});

const replyOrFooterActionItemsStyle = xcss({
	top: 'space.150',
});

const actionItemsStyle = xcss({
	top: 'space.200',
});

const hoveredBackgroundInSidePanel = xcss({
	background: token('elevation.surface.raised'),
});

const hoveredBackgroundOutOfSidePanel = xcss({
	background: token('elevation.surface'),
});

const selectedBackground = xcss({
	background: token('color.background.input.hovered'),
});

const softBorderBase = css({
	position: 'relative',
	maxHeight: 'inherit',
	borderRadius: token('radius.small'),
});

const resolvedCommentActionItemsStyle = xcss({
	top: 'space.100',
});

const softBorderPaddingOverride = xcss({
	paddingLeft: 'space.050',
});

export const useCommentActions = ({
	agentChatMenuItem,
	pageId,
	pageType,
	commentId,
	permissions,
	isReply,
	parentCommentId,
	restrictEdit,
	restrictDelete,
	deleteComment,
	editComment,
	resolveComment,
	mode,
	supportedActions,
	isCommentsPanel = false,
	isHovered,
	annotationId,
	resolveProperties,
	reopenComment,
	replyToComment,
	inheritedReactionsData,
	reactionsReadOnly = false,
	reactionsCacheUpdateFn,
	commentDateUrl,
	commentType,
	hideBranchingStyle = false,
	canAddComments = true,
	isSelected = false,
	isParentCommentOpen = true,
	isFooterComment = false,
	isStandaloneComment = false,
	isOffline = false,
	numReplies,
	isFabricPage = true,
	authorName,
	isArchived,
}: UseCommentActionsProps):
	| {
			mainCommentActions: React.JSX.Element;
			hoveredActionItems: React.JSX.Element;
			isDropdownOpen: boolean;
	  }
	| {
			mainCommentActions: React.JSX.Element;
			hoveredActionItems: null;
			isDropdownOpen: boolean;
	  } => {
	const deletePressTracing = usePressTracing('delete-comment-action');
	const editPressTracing = usePressTracing('start-edit-comment-action');
	const resolvePressTracing = usePressTracing('resolve-comment-action');

	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const effectiveHover = isHovered || isDropdownOpen;
	const [showCommentActionItems, setShowCommentActionItems] = useState(false);
	const [isEditable, setIsEditable] = useState(false);
	const [isRemovable, setIsRemovable] = useState(false);
	const [isResolvable, setIsResolvable] = useState(false);
	const [canBeReopened, setCanBeReopened] = useState(false);
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { isReactionsEnabled } = useContext(ReactionsContext);
	const [reactionsCount, setReactionsCount] = useState(inheritedReactionsData?.reactionsCount || 0);
	const [reactionParticleEffectByEmoji, setReactionParticleEffectByEmoji] = useState<
		Record<string, boolean>
	>({});

	const { hasContentChanged } = useCommentsContentState();
	const { resetContentChanged } = useCommentsContentActions();
	const { showModal } = useDialogs();

	const { handleReply, handleReplyWithoutMention } = useCommentReply({
		commentId,
		parentCommentId,
		isFooterComment,
		replyToComment,
	});

	const { updateReactionInCache } = useUpdateReactionInCache();

	const { edition, cloudId } = useSessionData();

	const { commentService } = useCommentService();

	const isLivePage = useIsCurrentPageLive();

	const [contentId] = usePageContentId();
	const [contentType] = useContentType();
	const { subType } = getCachedPageData(contentId);

	const { formatMessage } = useIntl();

	const handleGqlError = (error: Error) => {
		experienceTracker.stopOnError({
			name: VIEW_REACTIONS_EXPERIENCE,
			error,
		});
	};
	const { data: reactionsData, loading: reactionsLoading } = useQueryInterop<
		useCommentActionsReactionsCommentsQuery$data,
		useCommentActionsReactionsCommentsQuery$variables
	>(useCommentActionsReactionsCommentsQuery, {
		variables: { contentId: commentId, containerId: pageId },
		onError: handleGqlError,
		fetchPolicy: 'cache-and-network',
		// If we have the reactions from a higher component, skip the query
		skip: Boolean(inheritedReactionsData),
	});

	useEffect(() => {
		// If we have the reactions from a higher component, use that count
		const count =
			(inheritedReactionsData === undefined || inheritedReactionsData === null
				? reactionsData?.comments?.nodes?.[0]?.reactionsSummary?.reactionsCount
				: inheritedReactionsData?.reactionsCount) || 0;

		setReactionsCount(count);
	}, [reactionsData, inheritedReactionsData]);

	useEffect(() => {
		// Actions are decided by the mode we're displaying
		// and the permissions available for the user
		const canEdit = Boolean(
			permissions?.isEditable && editComment && supportedActions.includes('edit') && !isOffline,
		);
		const canDelete = Boolean(
			permissions?.isRemovable &&
			deleteComment &&
			supportedActions.includes('delete') &&
			!isOffline,
		);
		const canResolve = Boolean(
			permissions?.isResolvable &&
			resolveComment &&
			supportedActions.includes('resolve') &&
			!isOffline,
		);

		// if user doesn't have resolve comment permission then they shouldn't have reopen permission too
		const hasPermissionToReopen =
			reopenComment && supportedActions.includes('reopen') && permissions?.isResolvable;
		const canReopen = Boolean(hasPermissionToReopen && !isReply) && !isOffline;
		// handle reopening dangling comments
		if (resolveProperties?.resolved && resolveProperties?.isDangling) {
			setCanBeReopened(canReopen);

			// handle reopening non-dangling comments
		} else {
			// Slides render in an iframe, so their annotation marks are never in this document; under
			// `cc_comments_detached_slides_label` the thread list already says whether the content
			// still exists.
			setCanBeReopened(
				Boolean(
					canReopen &&
					resolveProperties?.resolved &&
					((annotationId && inlineCommentExists(annotationId)) ||
						commentType === CommentType.GENERAL ||
						(contentType === 'slide' &&
							expValEquals('cc_comments_detached_in_open_view', 'isEnabled', true) &&
							expValEquals('cc_comments_detached_slides_label', 'isEnabled', true))),
				),
			);
		}

		if (mode === 'view' || mode === 'edit') {
			if (!isReply) {
				setIsResolvable(canResolve); // Parent comments are resolvable if allowed
			} else {
				setIsResolvable(false); // Replies cannot be resolved
			}
			// Comments panel is only rendered for fabric pages not on the legacy
			if (isCommentsPanel || isFabricPage) {
				const hasPermissions = canEdit || canDelete || canResolve;
				const shouldShowDropdownMenu = !!effectiveHover && hasPermissions;

				// Set the state for using the dropdown menu
				setShowCommentActionItems(shouldShowDropdownMenu);
			} else if (!isReply && (canEdit || canDelete || canResolve)) {
				setShowCommentActionItems(true); // Parent comments or replies outside panel can show menu
			}

			// Users can delete the replies they created
			setIsRemovable(canDelete);
			setIsEditable(canEdit);
		} else {
			setIsRemovable(canDelete);
			setIsEditable(canEdit);
			setIsResolvable(canResolve && !isReply);
		}
	}, [
		mode,
		permissions,
		effectiveHover,
		isReply,
		isCommentsPanel,
		supportedActions,
		deleteComment,
		editComment,
		resolveComment,
		reopenComment,
		resolveProperties?.isDangling,
		annotationId,
		resolveProperties?.resolved,
		commentType,
		contentType,
		isOffline,
		isFabricPage,
	]);

	useEffect(() => {
		// If the user leaves this comment and the dropdown menu is open, close the dropdown menu so
		// they don't see it when they return
		if (isDropdownOpen && !effectiveHover) {
			setIsDropdownOpen(false);
		}
	}, [isCommentsPanel, isDropdownOpen, effectiveHover, setIsDropdownOpen]);

	const getInlineCommentModeConstant = () => {
		switch (mode) {
			case 'view':
				return InlineCommentMode.VIEW;
			case 'edit':
				if (isLivePage) {
					return InlineCommentMode.LIVE;
				}
				return InlineCommentMode.EDIT;
		}
	};

	const handleDelete = () => {
		if (commentType === CommentType.INLINE) {
			experienceTracker.start({
				name: DELETE_INLINE_COMMENT_EXPERIENCE,
				attributes: {
					mode: getInlineCommentModeConstant(),
					framework: InlineCommentFramework.ANNOTATION_PROVIDER,
					isReply,
					source: isCommentsPanel ? AnalyticsSource.COMMENTS_PANEL : AnalyticsSource.STANDALONE,
					annotationId,
				},
			});
		} else {
			experienceTracker.start({
				name: DELETE_PAGE_COMMENT_EXPERIENCE,
				attributes: {
					isReply,
				},
			});
		}

		showModal(CommentWarningDialog, {
			onConfirm: () => {
				deleteCommentWithPressTracing();
			},
			isDelete: true,
			onCancel: () => {
				// abort delete experience if dialog is closed
				experienceTracker.abort({
					name:
						commentType === CommentType.INLINE
							? DELETE_INLINE_COMMENT_EXPERIENCE
							: DELETE_PAGE_COMMENT_EXPERIENCE,
					reason: 'delete dialog closed',
				});
			},
		});
	};

	const deleteCommentWithPressTracing = () => {
		if (!!deleteComment) {
			deletePressTracing();
			deleteComment();
		}
	};

	const handleEditComment = () => {
		if (commentType === CommentType.INLINE) {
			startEditorExperiences(
				/* compoundExperience */ {
					name: EDIT_INLINE_COMMENT_EXPERIENCE,
					attributes: {
						mode: getInlineCommentModeConstant(),
						framework: InlineCommentFramework.ANNOTATION_PROVIDER,
						isReply,
						type: isReply ? 'reply' : 'topLevel',
						annotationId,
					},
				},
				/* editorLoadExperience */ {
					name: EDIT_INLINE_COMMENT_LOAD_EXPERIENCE,
				},
				/* editorPublishExperience */ {
					name: EDIT_INLINE_COMMENT_PUBLISH_EXPERIENCE,
				},
			);
		} else {
			startEditorExperiences(
				/* compoundExperience */ {
					name: EDIT_PAGE_COMMENT_EXPERIENCE,
					attributes: {
						editor: 'FABRIC',
						isSingleExperience: true,
						edition,
						type: isReply ? 'reply' : 'topLevel',
						source: isFooterComment ? AnalyticsSource.PAGE_FOOTER : AnalyticsSource.COMMENTS_PANEL,
					},
				},
				/* editorLoadExperience */ {
					name: EDIT_PAGE_COMMENT_LOAD_EXPERIENCE,
				},
				/* editorPublishExperience */ {
					name: EDIT_PAGE_COMMENT_PUBLISH_EXPERIENCE,
				},
			);
		}

		editComment && editComment();
	};

	const handleEditCommentWithPressTracing = () => {
		editPressTracing();
		handleEditComment();
	};

	const handleCopyLink = async () => {
		const linkToComment = `${window.location.origin}/wiki${commentDateUrl}`;
		await copyToClipboard(linkToComment);
	};

	const handleResolveComment = () => {
		const doResolve = () => {
			if (commentType === CommentType.INLINE) {
				experienceTracker.start({
					name: RESOLVE_INLINE_COMMENT_EXPERIENCE,
					id: commentId,
					attributes: {
						...getInlineCommentExperienceContext(
							annotationId,
							isLivePage ? 'live' : mode === 'edit' ? 'edit' : 'view',
						),
						mode: getInlineCommentModeConstant(),
						framework: InlineCommentFramework.ANNOTATION_PROVIDER,
						annotationId,
					},
				});
			} else {
				experienceTracker.start({
					name: RESOLVE_PAGE_COMMENT_EXPERIENCE,
					id: commentId,
					attributes: {
						annotationId,
					},
				});
			}

			resolveCommentWithPressTracing();
		};

		if (hasContentChanged) {
			showModal(CommentWarningDialog, {
				onConfirm: () => {
					resetContentChanged();
					doResolve();
				},
			});
		} else {
			doResolve();
		}
	};

	const resolveCommentWithPressTracing = () => {
		if (!!resolveComment) {
			resolvePressTracing();
			resolveComment();
		}
	};

	const handleReopenComment = () => {
		reopenComment && reopenComment();
	};

	const handleParticleEffectChange = useCallback((emojiId: string, show: boolean) => {
		setReactionParticleEffectByEmoji((prev) => ({
			...prev,
			[emojiId]: show,
		}));
	}, []);

	const renderEditAction = (isRestricted?: boolean, isDropdownMenu?: boolean) => {
		if (isDropdownMenu) {
			return isRestricted ? (
				<OverflowDisabledDropdownItem testId="disabled-edit-comment-dropdown-action">
					<DisabledCommentAction action="edit" isReply={isReply} isInOverflowMenu />
				</OverflowDisabledDropdownItem>
			) : (
				<DropdownItem
					shouldTitleWrap={false}
					testId="edit-comment-dropdown-action"
					data-cy="edit-comment-action"
					onClick={handleEditComment}
					interactionName="start-edit-comment-action"
				>
					<FormattedMessage {...i18n.edit} />
				</DropdownItem>
			);
		}

		return isRestricted ? (
			<DisabledCommentAction action="edit" isReply={isReply} isInOverflowMenu />
		) : (
			<ActionButton
				data-testid="edit-comment-action"
				data-cy="edit-comment-action"
				onClick={handleEditCommentWithPressTracing}
			>
				<FormattedMessage {...i18n.edit} />
			</ActionButton>
		);
	};

	const renderDeleteAction = (isRestricted?: boolean, isDropdownMenu?: boolean) => {
		if (isDropdownMenu) {
			return isRestricted ? (
				<OverflowDisabledDropdownItem testId="disabled-delete-comment-dropdown-action">
					<DisabledCommentAction action="delete" isReply={isReply} isInOverflowMenu />
				</OverflowDisabledDropdownItem>
			) : (
				<DropdownItem
					shouldTitleWrap={false}
					testId="delete-comment-dropdown-action"
					onClick={handleDelete}
				>
					<FormattedMessage {...i18n.delete} />
				</DropdownItem>
			);
		}

		return isRestricted ? (
			<DisabledCommentAction action="delete" isReply={isReply} />
		) : (
			<ActionButton
				data-testid="delete-comment-action"
				onClick={handleDelete}
				onMouseEnter={() => preloadDataRetentionPolicy({ cloudId })}
			>
				<FormattedMessage {...i18n.delete} />
			</ActionButton>
		);
	};

	const renderReopenComment = () => {
		return (
			canBeReopened && (
				<div css={itemStyles}>
					<IconButton
						icon={(iconProps: NewCoreIconProps) => (
							<StatusSuccessIcon
								{...iconProps}
								spacing="spacious"
								color={token('color.icon.success')}
								label={formatMessage(i18n.reopen)}
							/>
						)}
						label={formatMessage(i18n.reopen)}
						isTooltipDisabled={false}
						onClick={handleReopenComment}
						appearance="subtle"
						testId="reopen-comment-action"
						analyticsContext={{
							attributes: {
								name: ActionName.REOPEN_COMMENT,
								source: isCommentsPanel
									? AnalyticsSource.COMMENTS_PANEL
									: AnalyticsSource.STANDALONE,
								isDanglingComment: resolveProperties?.isDangling, // all dangling comments are inline comments
							},
						}}
					/>
				</div>
			)
		);
	};

	const renderReplyAction = () => {
		return (
			<ReplyContainer>
				<ActionButton
					data-testid="reply-comment-action"
					data-cy="reply-comment-action"
					onClick={handleReply}
				>
					<FormattedMessage {...i18n.replyPlaceholder} />
				</ActionButton>
			</ReplyContainer>
		);
	};

	const renderCopyButton = () => {
		return (
			<DropdownItem
				shouldTitleWrap={false}
				testId="copy-link-dropdown-action"
				onClick={handleCopyLink}
			>
				<FormattedMessage {...i18n.copyLink} />
			</DropdownItem>
		);
	};

	const renderResolveOption = () => {
		if (isCommentsPanel && isReply) {
			return;
		}
		return (
			<DropdownItem
				shouldTitleWrap={false}
				testId="resolve-comment-dropdown-action"
				onClick={handleResolveComment}
			>
				<FormattedMessage {...i18n.resolve} />
			</DropdownItem>
		);
	};

	const renderDropdownMenu = (editRestricted?: boolean, deleteRestricted?: boolean) => {
		// hide the dropdown if not the comments panel and does not have permissions to edit/delete/resolve
		const hasNoCommmentPermissions = !isEditable && !isRemovable && (isReply || !isResolvable);

		// For old UI, use the existing logic
		const shouldHideDropdownForStandalone = !isCommentsPanel && hasNoCommmentPermissions;

		if (shouldHideDropdownForStandalone) {
			return <></>;
		}

		return (
			<span data-cy="comment-overflow-actions-menu" data-testid="comment-overflow-actions-menu">
				<DropdownMenu
					strategy="absolute"
					shouldRenderToParent
					testId="comment-overflow-menu"
					isOpen={isDropdownOpen}
					zIndex={501} //Same zindex as SidebarContainer from confluence/next/packages/inline-comments-common/src/styled-components.tsx so the overflow menu doesn't sit under the inline comment sidebar
					onOpenChange={({ isOpen, event }) => {
						event?.stopPropagation();
						setIsDropdownOpen(isOpen);
						if (isRemovable) void preloadDataRetentionPolicy({ cloudId });
					}}
					trigger={({ triggerRef, ...props }) => (
						<IconButton
							{...props}
							ref={triggerRef}
							testId="comment-overflow-menu-trigger"
							label={formatMessage(i18n.commentActionsMenuLabel)}
							appearance="subtle"
							spacing="compact"
							icon={ShowMoreHorizontalIcon}
							isTooltipDisabled={false}
							isSelected={isDropdownOpen}
							analyticsContext={{
								attributes: {
									name: 'moreActionsButton',
									source: isCommentsPanel
										? AnalyticsSource.COMMENTS_PANEL
										: AnalyticsSource.STANDALONE,
									commentType,
								},
							}}
						/>
					)}
				>
					<DropdownItemGroup>
						{isFabricPage && renderCopyButton()}
						{agentChatMenuItem}
						{!isFabricPage && !isFooterComment && isResolvable && renderResolveOption()}
						{isEditable && renderEditAction(editRestricted, showCommentActionItems)}
						{isRemovable && renderDeleteAction(deleteRestricted, showCommentActionItems)}
					</DropdownItemGroup>
				</DropdownMenu>
			</span>
		);
	};

	const reactionsUpdateFn = useMemo(() => {
		if (reactionsCacheUpdateFn) {
			return reactionsCacheUpdateFn;
		}

		return inheritedReactionsData !== undefined && isCommentsPanel
			? (
					emojiId: string,
					actionType: 'add' | 'delete',
					objectId: string,
					containerId?: string,
					result?: ReactionsMutationResults,
					onCacheUpdateError?: (error: Error) => void,
				) => {
					// This shouldn't happen because no result means the mutation failed, but the test makes
					// TypeScript happy
					if (!result) {
						return;
					}

					// Update particle effect state when adding/removing reactions
					setReactionParticleEffectByEmoji((prev) => ({
						...prev,
						[emojiId]: actionType === 'add',
					}));

					if (annotationId && emojiId && actionType) {
						// Update the reaction in the content types bridge
						void commentService?.onUpdateReaction(annotationId, emojiId, actionType);
					}

					// Update the GQL cache using the Comments Data Manager
					return updateReactionInCache({
						mutationResult: result,
						commentId: objectId,
						pageId: containerId || '',
						contentType: 'page',
						actionType,
						emojiId,
						onError: onCacheUpdateError,
					});
				}
			: undefined;
	}, [
		inheritedReactionsData,
		reactionsCacheUpdateFn,
		isCommentsPanel,
		annotationId,
		commentService,
		updateReactionInCache,
	]);

	const renderReplyButton = () => {
		const labelText = formatMessage(i18n.replyPlaceholderWithName, {
			name: authorName || formatMessage(commentsi18n.anonymous),
		});
		return (
			<Box testId="comment-reply-button-container" aria-label={labelText}>
				<IconButton
					testId="comment-reply-button"
					label={formatMessage(i18n.replyPlaceholderWithName, {
						name: authorName || formatMessage(commentsi18n.anonymous),
					})}
					appearance="subtle"
					spacing="compact"
					icon={ReplyLeftIcon}
					onClick={handleReply}
					isTooltipDisabled={false}
				/>
			</Box>
		);
	};

	const renderResolveActionButton = () => {
		return (
			<IconButton
				testId="comment-resolve-action-item"
				label={formatMessage(i18n.resolveThread)}
				appearance="subtle"
				spacing="compact"
				icon={CheckMarkIcon}
				onClick={handleResolveComment}
				isTooltipDisabled={false}
				css={{ padding: 0 }}
				analyticsContext={{
					attributes: {
						name: 'resolveActionsButton',
						source: isCommentsPanel ? AnalyticsSource.COMMENTS_PANEL : AnalyticsSource.STANDALONE,
						commentType,
					},
				}}
			/>
		);
	};

	const renderReactions = ({
		readOnly = false,
	}: {
		readOnly?: boolean;
	} = {}) => {
		const reactionPickerPlacement =
			isCommentsPanel && !isFooterComment ? 'bottom-end' : 'bottom-start';

		return (
			<ReactionsContainer
				role="group"
				hasReactions={reactionsCount > 0}
				isCommentsPanel={isFabricPage}
				onClick={(e) =>
					// Stop the click event from propagating to the outer comments container to prevent triggering the scroll-to-comment function
					e.stopPropagation()
				}
				aria-label={formatMessage(i18n.reactionAriaLabelWithName, {
					name: authorName || formatMessage(commentsi18n.anonymous),
				})}
			>
				<Reactions
					sort="desc"
					readOnly={readOnly || reactionsReadOnly}
					contentId={commentId}
					containerId={pageId}
					containerType={
						// The reactions API has no slide container type. Slides keep their comment
						// reactions under a whiteboard container, as the slides iframe does when it
						// saves a reaction.
						pageType === 'slide' &&
						expValEquals('cc_comments_slide_panel_reactions', 'isEnabled', true)
							? ReactionContainerType.WHITEBOARD
							: (pageType as ReactionContainerType)
					}
					reactionPickerPlacement={reactionPickerPlacement}
					location={
						mode === 'edit'
							? ReactionLocation.EDITOR_INLINE_COMMENT
							: ReactionLocation.INLINE_COMMENT
					}
					hidePlaceholder={reactionsCount === 0 || inheritedReactionsData !== undefined}
					reactionsData={
						inheritedReactionsData === undefined || inheritedReactionsData === null
							? (reactionsData?.comments?.nodes?.[0]?.reactionsSummary as ReactionsData)
							: inheritedReactionsData
					}
					updateCache={reactionsUpdateFn}
					getOptimisticResponse={getOptimisticResponse}
					onlyRenderPicker={false}
					isSubtle
					showAddReactionText={false}
					customAnalyticsAttributes={{
						source: isCommentsPanel ? AnalyticsSource.COMMENTS_PANEL : AnalyticsSource.STANDALONE,
						isReply,
						mode,
					}}
					contentSubType={subType}
					isListItem={false}
					particleEffectByEmoji={reactionParticleEffectByEmoji}
					onParticleEffectChange={handleParticleEffectChange}
				/>
			</ReactionsContainer>
		);
	};

	// An abstraction made by tracing through the custom xcss returned in renderActionItemsOnHover to return the correct
	// background color. When migrating to compiled, the conditional styling needs to be simplified to reduce duplication
	const getActionItemsBackgroundColor = () => {
		if (isCommentsPanel) {
			if (isSelected) {
				return token('color.background.input.hovered');
			} else if (effectiveHover) {
				return token('elevation.surface.raised');
			}
		}
		return token('elevation.surface');
	};

	const renderActionItemsOnHover = () => (
		<Box
			data-testId="comment-action-items"
			xcss={[
				rightAlignedActionItemsStyle,
				clickableItemStyle,
				effectiveHover &&
					(isFooterComment || isStandaloneComment || !isCommentsPanel
						? hoveredBackgroundOutOfSidePanel
						: hoveredBackgroundInSidePanel),
				isSelected && isCommentsPanel && selectedBackground,
				isFooterComment && rightAlignedActionItemsFooterCommentStyle,
				isFabricPage && !isFooterComment && rightAlignedActionItemsCommentsPanelStyle,
				!isCommentsPanel && !isFooterComment && isFabricPage && rightAlignedItemsStandaloneStyle,
				isCommentsPanel && isReply && rightAlignedActionItemsFooterCommentStyle,
				expValEquals('confluence_cohiti_comment_hover_actions', 'isEnabled', true) &&
					softBorderPaddingOverride,
				actionItemsStyle,
				isCommentsPanel && !isReply && commentsPanelActionItemsStyle,
				(isReply || isFooterComment) && replyOrFooterActionItemsStyle,
				isStandaloneComment &&
					!isReply &&
					!!resolveProperties?.resolvedTime &&
					resolvedCommentActionItemsStyle,
			]}
		>
			<div
				css={[
					itemStyles,
					expValEquals('confluence_cohiti_comment_hover_actions', 'isEnabled', true) &&
						softBorderBase,
				]}
				style={{
					boxShadow: expValEquals('confluence_cohiti_comment_hover_actions', 'isEnabled', true)
						? `-10px 0 10px 0px ${getActionItemsBackgroundColor()}`
						: '',
				}}
				data-testId="comment-hover-actions-container"
			>
				{!resolveProperties?.resolved &&
					(!isReply || isFooterComment || isCommentsPanel) && // Footer replies can be replied to
					!isArchived &&
					canAddComments &&
					renderReplyButton()}

				{!isReply && !resolveProperties?.resolved && isResolvable && (
					<div>{renderResolveActionButton()}</div>
				)}
				<div>{renderDropdownMenu(restrictEdit, restrictDelete)}</div>
			</div>
		</Box>
	);

	const renderStaticActionsItemsForCommentsPanel = () => {
		const isResolved = resolveProperties?.resolved;
		return (
			<>
				{isResolved && (
					<Box
						xcss={[
							rightAlignedActionItemsStyle,
							isFabricPage && !isFooterComment && rightAlignedActionItemsCommentsPanelStyle,
							isFooterComment && rightAlignedActionItemsFooterCommentStyle,
						]}
						testId="static-actions-items"
					>
						{renderReopenComment()}
					</Box>
				)}
			</>
		);
	};

	const getCommentsPanelActionItems = () => {
		const isReadOnlyForResolved = resolveProperties?.resolved || (isReply && !isParentCommentOpen);

		const commonElements = (
			<>
				{isReactionsEnabled &&
					!reactionsLoading &&
					(reactionsCount > 0 || !isReadOnlyForResolved) && (
						<Box
							xcss={[
								reactionBelowCommentBodyStyle,
								isFabricPage && reactionInCommentPanelStyle,
								isCommentsPanel && isFabricPage && panelReactionMarginStyle,
								isFabricPage && isReply && replyReactionInCommentPanelStyle,
								isFabricPage &&
									!isReply &&
									((!hideBranchingStyle && canAddComments) ||
										(isReadOnlyForResolved && numReplies !== 0)) &&
									withLeftBorderStyle,
								// eslint-disable-next-line confluence-feature-gating/no-preconditioning
								isFabricPage &&
									!isReply &&
									((!hideBranchingStyle && canAddComments) ||
										(isReadOnlyForResolved && numReplies !== 0)) &&
									// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
									fg('confluence_comment_border_migration') &&
									withLeftBorderStyleUpdated,
								!isCommentsPanel && isFabricPage && standaloneReactionMarginStyle,
								!isCommentsPanel &&
									!isFooterComment &&
									hideBranchingStyle &&
									isFabricPage &&
									customPaddingStyle,
								!isReply && numReplies === 0 && multipleActionsStyle,
								!isCommentsPanel && !isReply && standaloneParentStyle,
								!isCommentsPanel && numReplies === 0 && standaloneParentWithNoRepliesStyle,
							]}
						>
							{!isReply &&
								numReplies === 0 &&
								canAddComments &&
								isParentCommentOpen &&
								!isStandaloneComment && (
									<ReplyButton
										spacing="compact"
										isOffline={isOffline}
										handleReplyButtonClick={handleReplyWithoutMention}
									/>
								)}
							{renderReactions({
								readOnly: isReadOnlyForResolved || isOffline,
							})}
						</Box>
					)}
			</>
		);

		// no hover state for resolved comments
		const shouldRenderHoverState = () => {
			if (isReply && !isParentCommentOpen) {
				return false;
			} else {
				return effectiveHover && !resolveProperties?.resolved;
			}
		};

		const stateSpecificElements = shouldRenderHoverState()
			? renderActionItemsOnHover()
			: renderStaticActionsItemsForCommentsPanel();

		return {
			mainCommentActions: commonElements,
			hoveredActionItems: stateSpecificElements,
			isDropdownOpen,
		};
	};

	const renderStandaloneCommentActions = () => {
		return (
			<ActionsContainer>
				<ActionItem aria-hidden="true" />
				{!isReply && isResolvable && (
					<ActionItem mode={mode} isReply={isReply}>
						<ActionButton
							data-testid="resolve-comment-action"
							data-cy="resolve-comment-action"
							onClick={handleResolveComment}
						>
							<FormattedMessage {...i18n.resolve} />
						</ActionButton>
					</ActionItem>
				)}
				{!showCommentActionItems && isEditable && (
					<ActionItem mode={mode} isReply={isReply}>
						{renderEditAction(restrictEdit, showCommentActionItems)}
					</ActionItem>
				)}
				{!showCommentActionItems && isRemovable && (
					<ActionItem mode={mode} isReply={isReply}>
						{renderDeleteAction(restrictDelete, showCommentActionItems)}
					</ActionItem>
				)}
				{isReactionsEnabled &&
					!reactionsLoading &&
					!resolveProperties?.resolved &&
					renderReactions({
						readOnly: isOffline,
					})}
				{!resolveProperties?.resolved ||
					// eslint-disable-next-line confluence-feature-gating/no-preconditioning
					(isCommentsPanel && renderReplyAction())}
			</ActionsContainer>
		);
	};

	const renderStandaloneCommentActionItems = () => {
		return (
			<>
				{!isReply && renderDropdownMenu(restrictEdit, restrictDelete)}
				{renderStandaloneCommentActions()}
			</>
		);
	};

	return isFabricPage
		? getCommentsPanelActionItems()
		: {
				mainCommentActions: renderStandaloneCommentActionItems(),
				hoveredActionItems: null,
				isDropdownOpen,
			};
};
